import React from "react";
import {
  AvatarGroup,
  Box,
  Badge,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  CalendarToday as CalendarIcon,
  ChatBubbleOutline as CommentIcon,
  AttachFile as AttachmentIcon,
  AccountTree as FlownodeIcon,
  ListAltRounded as FieldIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Task } from "model/activitiesModel";
import { getTranslation } from "common";
import {
  getParticipants,
  getInitials,
  getDisplayName,
  getLabelDataFromJsonTags,
} from "common/utilities";
import AvatarRem from "components/AvatarRem";
import TaskQuickSwapButtons from "components/TaskQuickSwapButtons";
import Labels from "components/labels/Labels";

interface MobileTaskCardProps {
  task: Task;
  onClick: () => void;
  onNext?: (task: Task) => void | Promise<void>;
  onBack?: (task: Task) => void | Promise<void>;
  translations: Record<string, string>;
}

/**
 * Mobile task card (portrait). Same layout as the landscape
 * `MobileTaskCardCompact` (no inline assignee row — avatars in the
 * footer carry the participant info).
 *
 *   row 1 (header): activity name (flownodename) + BUG FIX (left),
 *                   date + priority + kebab (right)
 *   row 2 (title):  description (process name) — bold, LEFT-aligned, 2 lines max
 *   row 3:         small flownode icon + processmodelname (model name)
 *   row 4 (footer): comment/field/attachment icons + avatars + quick swap
 *
 * The description / processmodelname slots mirror the desktop dialog:
 *   description is what the user calls "process name" (the bold
 *   headline in the centre of the card), and processmodelname sits
 *   underneath it, prefixed by the flownode icon.
 */
const MobileTaskCard: React.FC<MobileTaskCardProps> = ({
  task,
  onClick,
  onNext,
  onBack,
  translations,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const date = task.created ? dayjs(task.created).format("DD/MM") : "";
  const participants = getParticipants(task.jsonparticipant);

  const priorityColor = theme.palette.primary.main;
  const priorityBorder = `1.5px solid ${priorityColor}`;
  const priorityBg = isDark ? `${priorityColor}24` : "transparent";
  const priorityText = isDark ? theme.palette.primary.light : theme.palette.text.primary;

  const isDoing = task.state === 1;
  // Some backends may not populate `bugfix` on the task model; fall back to false.
  const isBugFix = Boolean((task as Task & { bugfix?: boolean }).bugfix);

  const icons = [
    {
      title: getTranslation(translations, "settings.comments.label"),
      IconComponent: CommentIcon,
      count: task.countcomments ?? 0,
    },
    {
      title: getTranslation(translations, "generic.fields.caption"),
      IconComponent: FieldIcon,
      count: task.countuserdatafields ?? 0,
    },
    {
      title: getTranslation(translations, "generic.attached_files.caption"),
      IconComponent: AttachmentIcon,
      count: task.countattachments ?? 0,
    },
  ];

  const activityName = task.flownodename || task.name || "";
  const description = task.description || "";
  const processModelName = task.processmodelname || "";
  const labels = getLabelDataFromJsonTags(task.jsontag);

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        p: 1.5,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:active": { transform: "scale(0.99)" },
        "&:hover": { boxShadow: 3 },
      }}
    >
      {/* Header row: activity name + BUG FIX (left), date + priority + kebab (right) */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          {activityName && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: 0.4,
                fontSize: 10,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {activityName}
            </Typography>
          )}
          {isBugFix && (
            <Chip
              label="BUG FIX"
              size="small"
              sx={{
                height: 16,
                fontSize: 8,
                fontWeight: 800,
                color: "primary.main",
                border: `1px solid ${theme.palette.primary.main}`,
                bgcolor: "transparent",
                borderRadius: 0.5,
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
          {date && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary" }}>
              <CalendarIcon sx={{ fontSize: 12 }} />
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                {date}
              </Typography>
            </Stack>
          )}
          {task.priority_name && (
            <Chip
              size="small"
              label={task.priority_name.length > 6 ? `${task.priority_name.substring(0, 6)}…` : task.priority_name}
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 700,
                border: priorityBorder,
                bgcolor: priorityBg,
                color: priorityText,
                borderRadius: 0.75,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
          <Tooltip title={getTranslation(translations, "generic.detail.caption") || "Dettagli"}>
            <IconButton
              id={`mobile-task-detail-btn-${task.idprocesstoken}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
              sx={{
                width: 24,
                height: 24,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0.75,
              }}
            >
              <MoreHorizIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Title (description = "process name"), bold, LEFT-aligned, 2 lines max */}
      {description && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.35,
            textAlign: "left",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </Typography>
      )}

      {/* Process model: small flownode icon + name — mirrors the desktop
          `TaskCard` row with the AccountTree icon. */}
      {processModelName && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <FlownodeIcon sx={{ fontSize: 12, color: "primary.main", flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {processModelName}
          </Typography>
        </Stack>
      )}

      {labels.length > 0 && (
        <Box sx={{ minWidth: 0, overflow: "hidden" }}>
          <Labels labelCompleteMode inCard labels={labels} />
        </Box>
      )}

      {/* Footer row: icons (left) + avatars (mid) + quick swap (right) */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
        sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}
      >
        {/* Icon row */}
        <Stack direction="row" alignItems="center" spacing={0.25}>
          {icons.map(({ title, IconComponent, count }) => (
            <Tooltip key={title} title={title} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                sx={{ p: 0.5 }}
              >
                <Badge
                  badgeContent={count}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": { fontSize: 8, height: 12, minWidth: 12, px: 0.25 },
                  }}
                >
                  <IconComponent sx={{ fontSize: 14, color: "text.secondary" }} />
                </Badge>
              </IconButton>
            </Tooltip>
          ))}
        </Stack>

        {/* Participant avatars */}
        {participants.length > 0 && (
          <Tooltip
            arrow
            title={participants.map((p) => getDisplayName(p)).join(", ")}
            sx={{ mr: "auto", ml: 0.5 }}
          >
            <AvatarGroup
              max={3}
              sx={{
                "& .MuiAvatar-root": {
                  width: 20,
                  height: 20,
                  fontSize: "0.65rem",
                  border: "1px solid",
                  borderColor: "background.paper",
                },
              }}
            >
              {participants.slice(0, 3).map((p) => {
                let iconUrl = `/users/${p.idparticipant}/picture`;
                if (p.participanttype === 1) iconUrl = `/downloads/groups/${p.idparticipant}`;
                else if (p.participanttype === 2) iconUrl = `/downloads/roles/${p.idparticipant}`;
                return (
                  <AvatarRem key={p.idparticipant} icon={iconUrl}>
                    {getInitials(p)}
                  </AvatarRem>
                );
              })}
            </AvatarGroup>
          </Tooltip>
        )}

        {/* Quick swap buttons (next/back) */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TaskQuickSwapButtons
            task={task}
            onBack={isDoing && onBack ? onBack : undefined}
            onNext={onNext}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default MobileTaskCard;
